import { message, Table } from 'antd';
import _ from 'lodash';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { TableStore } from 'stores/abstract';
import { PitstopTableCacheStore } from 'stores/CacheStore';

import { columnTextFilterProps } from './columnTextFilterProps';
import {
  editableGridComponents,
  getEditableGridActionColumn,
} from './EditableGrid';
import { titleWithSearch } from './titleWithSearch';

const TableContainer = styled(Table)`
  display: grid;
  overflow: auto;

  .ant-table-selection-column {
    width: 70px;
  }
  .ant-table-tbody tr {
    cursor: pointer;
  }

  .ant-table-pagination.ant-pagination {
    text-align: center;
    float: none;
    display: flex;
    justify-content: center;
  }

  .ant-pagination-options {
    display: none;
  }

  @media (max-width: 768px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px;
    }
  }
`;

const PitstopTable = observer((props) => {
  const [editingRowKey, setEditingRowKey] = useState(null);

  const {
    id,
    data,
    fetchData,
    columns,
    rowKey,
    expandedRowRender,
    height,
    onRowClick,
    onDelete,
    onEdit,
    rowSelection,
    expandIconAsCell,
    expandIconColumnIndex,
    pagination,
    scroll = {},
    components: propsComponents,
    filter: propsFilter,
    sort: propsSort,
    limit: propsLimit,
    getTableStore,
    searchValue,
    timeRange,
    hasNotification,
    onClickNotification,
    tableLayout,
  } = props;

  const getTableStoreInstance = () => {
    if (!PitstopTableCacheStore.data.has(id)) {
      PitstopTableCacheStore.setData(id, new TableStore());
    }
    return PitstopTableCacheStore.getData(id);
  };

  const tableStore = useMemo(() => {
    return props.tableStore || getTableStoreInstance();
  }, [props.tableStore, id]);

  const dataSource = useMemo(() => {
    return _.filter(data, Boolean);
  }, [data]);

  const load = async () => {
    if (!fetchData || typeof fetchData !== 'function') {
      console.error('fetchData is not a valid function in PitstopTable load');
      return;
    }

    const shouldFetch = !runInAction(() => tableStore?.loaded);

    if (shouldFetch) {
      const filter = { ...propsFilter };
      const filters = {
        offset: 0,
        limit: propsLimit || 10,
        sort: propsSort,
      };

      if (!_.isEmpty(filter)) {
        filters.filter = filter;
      }

      await fetchData(filters, tableStore);
    }

    if (getTableStore && typeof getTableStore === 'function') {
      getTableStore(tableStore);
    }
  };

  const reloadData = async () => {
    if (!fetchData || typeof fetchData !== 'function') {
      console.error('fetchData is not a valid function in PitstopTable');
      return;
    }

    const filter = { ...propsFilter };

    const filters = {
      offset: 0,
      limit: propsLimit || 10,
      sort: propsSort,
    };

    if (!_.isEmpty(filter)) {
      filters.filter = filter;
    }

    await fetchData(filters, tableStore);

    if (getTableStore && typeof getTableStore === 'function') {
      getTableStore(tableStore);
    }
  };

  const handleTableChange = async (pagination, filter, sort) => {
    if (!fetchData || typeof fetchData !== 'function') {
      console.error('fetchData is not a valid function in handleTableChange');
      return;
    }

    let column = null;
    const offset = (pagination.current - 1) * pagination.pageSize;

    if (sort.columnKey) {
      let sortString = sort.columnKey;
      const sortColumn = _.filter(
        columns,
        (el) => sort.columnKey === (el.sortLabel || el.dataIndex || el.key)
      )[0];

      if (!sortColumn) {
        sortString =
          sortColumn.sortLabel || sortColumn.dataIndex || sortColumn.key;
      }

      if (!sort.order) {
        column = undefined;
      } else {
        column = sort.order === 'ascend' ? `${sortString}` : `-${sortString}`;
      }
    }

    runInAction(() => {
      tableStore.reset();
    });
    await fetchData(
      {
        offset: offset,
        limit: pagination.pageSize,
        sort: column,
        filter: {
          ...runInAction(() => tableStore?.filter),
          ...propsFilter,
          ...filter,
        },
      },
      tableStore
    );
  };

  const getSortOrder = (column) => {
    if (!tableStore?.sort) {
      return false;
    }
    const index = tableStore?.sort.indexOf(column);
    if (index === -1) {
      return false;
    } else if (index === 0) {
      return 'ascend';
    } else {
      return 'descend';
    }
  };

  const tableColumns = useMemo(() => {
    const antdGridColumns = _.map(columns, (c) => {
      const onCell = (record, rowIndex) => {
        if (c.onCell) {
          c.onCell(record, rowIndex);
        }
        let onCellObject = {};
        if (c.editable) {
          onCellObject = {
            record,
            inputType: c.inputType,
            dataIndex: c.dataIndex,
            selectOptions: c.selectOptions,
            title: c.title,
            editing: record[rowKey] === editingRowKey,
          };
        }
        if (onRowClick && c.rowClick !== false) {
          onCellObject = {
            ...onCellObject,
            onClick: (ev) => {
              ev.preventDefault();
              onRowClick(record, ev);
            },
          };
        }
        return onCellObject;
      };

      const columnFilterProps = columnTextFilterProps(
        c.dataIndex || c.key,
        c.title
      );

      return {
        ...c,
        key: c.key || c.dataIndex,
        ...(c.sorter && {
          sortOrder: getSortOrder(c.sortLabel || c.dataIndex || c.key),
        }),
        title: columns.some((c) => c.filter)
          ? titleWithSearch({
              ...c,
              onChange: (e) => {
                columnFilterProps.dropDownComponent.props.onChange(e);
              },
              tableStore,
            })
          : c.title,
        ...(c.filter && columnFilterProps),
        onCell,
      };
    });

    if (columns.some((c) => c.editable)) {
      antdGridColumns.push(
        getEditableGridActionColumn({
          isEditing: (row) => row[rowKey] === editingRowKey,
          onDelete: async (row) => {
            try {
              if (onDelete) {
                await onDelete(row);
                message.success('Deleted successfully');
                if (fetchData && typeof fetchData === 'function') {
                  fetchData({}, tableStore);
                }
              }
            } catch {
              message.error('Delete failed');
            }
          },
          onCancel: () => setEditingRowKey(null),
          onEdit: (row) => setEditingRowKey(row[rowKey]),
          onSave: async (row, formValue) => {
            let changes = Object.keys(formValue).reduce((changed, key) => {
              return formValue[key] !== row[key]
                ? { ...changed, [key]: formValue[key] }
                : changed;
            }, {});
            if (Object.keys(changes).length) {
              changes[rowKey] = row[rowKey];
            } else {
              changes = null;
            }
            if (onEdit) {
              await onEdit({
                changes,
                newValue: changes ? { ...row, ...changes } : row,
                oldValue: row,
              });
            }
            setEditingRowKey(null);
          },
          hasNotification,
          onClickNotification,
        })
      );
    }
    return antdGridColumns;
  }, [columns, editingRowKey, rowKey, onRowClick, tableStore]);

  useEffect(() => {
    load();

    // reload current state if there is difference amount of data (delete record)
    if (data.length !== runInAction(() => tableStore?.data?.length)) {
      if (fetchData && typeof fetchData === 'function') {
        fetchData(
          {
            offset: 0,
            limit: runInAction(() => tableStore?.limit || 10),
            filter: runInAction(() => tableStore?.filter || undefined),
            sort: runInAction(() => tableStore?.sort),
          },
          tableStore
        );
      }
    }
  }, [id]);

  useEffect(() => {
    if (
      !_.isEqual(searchValue, props.searchValue) ||
      !_.isEqual(timeRange, props.timeRange)
    ) {
      runInAction(() => {
        tableStore.reset();
      });
      load();
    }

    // make sure the table is loaded
    const shouldLoad = runInAction(
      () => !tableStore?.loaded && !tableStore?.pending
    );
    if (shouldLoad) {
      load();
    }
  }, [searchValue, timeRange, tableStore?.loaded, tableStore?.pending]);

  const tableData = runInAction(() => {
    return {
      count: tableStore?.count || 0,
      offset: tableStore?.offset || 0,
      limit: tableStore?.limit || propsLimit || 10,
      loaded: tableStore?.loaded || false,
    };
  });

  const { count, offset, limit, loaded } = tableData;

  const tableProps = {
    columns: tableColumns,
    // Added these changes, because when displaying work orders that do not have uploaded files,
    // the filter tries to filter out values in which events.current_status == NO_FILE, but from
    // the backend comes events == null
    dataSource: dataSource.map((item) => {
      if (item.events === null) {
        return {
          ...item,
          events: {
            current_status: 'NO_FILE',
          },
        };
      }
      return item;
    }),
    loading: !loaded,
    rowKey,
    pagination: {
      pageSize: limit || propsLimit,
      total: count,
      current: offset / (limit || propsLimit) + 1,
      showSizeChanger: false,
      position: ['center'],
      ...pagination,
    },
    onChange: handleTableChange,
    scroll: { x: true, ...scroll },
    expandIconAsCell,
    expandIconColumnIndex,
    components: propsComponents || {},
    tableLayout,
  };

  if (columns.some((c) => c.editable)) {
    tableProps.components = editableGridComponents;
  }

  tableProps.components = {
    ...tableProps.components,
    ...propsComponents,
  };

  if (expandedRowRender) {
    tableProps.expandedRowRender = expandedRowRender;
    delete tableProps['scroll'];
  }

  if (!_.isNil(rowSelection)) {
    tableProps.rowSelection = rowSelection;
  }

  if (height) {
    tableProps.scroll = { ...tableProps.scroll, y: height };
  }
  if (props.expandedRowKeys !== undefined) {
    tableProps.expandedRowKeys = props.expandedRowKeys;
  }
  if (props.onExpand !== undefined) {
    tableProps.onExpand = props.onExpand;
  }
  if (props.onExpandedRowsChange !== undefined) {
    tableProps.onExpandedRowsChange = props.onExpandedRowsChange;
  }
  return <TableContainer {...tableProps} />;
});

PitstopTable.propTypes = {
  tableStore: PropTypes.instanceOf(TableStore),
  id: PropTypes.string.isRequired,
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  fetchData: PropTypes.func.isRequired,
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  expandedRowRender: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  height: PropTypes.number,
  onRowClick: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  getTableStore: PropTypes.func,
  rowSelection: PropTypes.object,
  rows: PropTypes.array,
  limit: PropTypes.number,
  noInitialLoad: PropTypes.bool,
  rawData: PropTypes.bool,
  filter: PropTypes.object,
  sort: PropTypes.string,
  expandIconAsCell: PropTypes.bool,
  expandIconColumnIndex: PropTypes.number,
  pagination: PropTypes.object,
  searchValue: PropTypes.string,
  timeRange: PropTypes.any,
  hasNotification: PropTypes.bool,
  onClickNotification: PropTypes.func,
  scroll: PropTypes.object,
  hideTopPagination: PropTypes.bool,
  components: PropTypes.object,
  tableLayout: PropTypes.string,
};

PitstopTable.defaultProps = {
  noInitialLoad: false,
  rawData: false,
  scroll: {},
  tableLayout: 'auto',
};

export default PitstopTable;
